import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

// UI Components
import Stack, { HStack } from '../ui/Stack/Stack';
import Typography from '../ui/Typography/Typography';
import Button from '../ui/Button/Button';
import Icon from '../ui/Icon/Icon';
import Badge from '../ui/Badge/Badge';
import Alert from '../ui/Alert/Alert';
import { FiUploadCloud } from 'react-icons/fi';

const ContributionChecklist = ({ job, dataSourceStatuses = [] }) => {
  const navigate = useNavigate();
  const { hasPermission, getImpliedSourceType } = useAuth();
  
  // Determine user capabilities based on permissions and context
  const canContributeGlobally = hasPermission('data:upload');
  const stewardSourceType = getImpliedSourceType();

  const handlePrepareData = (dataSourceTypeName) => {
    // Navigate to the data preparation page for the specific data source and batch
    navigate(`/user/batch/${job.id}/prepare/${dataSourceTypeName}`);
  };

  return (
    <Stack spacing="md">
      <Typography color="secondary">
        Review the status of each required data contribution for this job.
      </Typography>
      {dataSourceStatuses && dataSourceStatuses.length > 0 ? (
        <Stack spacing="sm">
          {dataSourceStatuses.map(dst => {
            const isSubmitted = dst.is_data_submitted;
            
            // A user can contribute to a specific category if:
            // 1. They have the general 'data:upload' permission, AND
            // 2. Either they are NOT a steward (i.e., they are a DLO/Admin), OR
            // 3. They ARE a steward for this specific data source type.
            const canContributeThisCategory = 
              canContributeGlobally && 
              (!stewardSourceType || stewardSourceType === dst.dst_name);
            
            return (
              <HStack 
                key={dst.dst_name} 
                justifyContent="space-between" 
                alignItems="center" 
                p="sm" 
                style={{
                  border: '1px solid var(--cipher-color-border-subtle)',
                  borderRadius: 'var(--cipher-border-radius-base)'
                }}
              >
                <Stack>
                    <Typography fontWeight="medium">{dst.display_name}</Typography>
                    {isSubmitted && dst.last_submission_details && (
                        <Typography variant="caption" color="secondary">
                            Last submitted by {dst.last_submission_details.submitted_by_username || 'N/A'} on {new Date(dst.last_submission_details.submitted_at).toLocaleDateString()}
                        </Typography>
                    )}
                </Stack>
                <HStack spacing="md" alignItems="center">
                  <Badge variant={isSubmitted ? 'success' : 'default'}>
                    {isSubmitted ? 'Submitted' : 'Pending'}
                  </Badge>
                  {canContributeThisCategory && (
                    <Button 
                      size="sm" 
                      variant={isSubmitted ? 'secondary' : 'primary'} 
                      onClick={() => handlePrepareData(dst.dst_name)}
                      leftIcon={<Icon as={FiUploadCloud} />}
                      title={isSubmitted ? `Contribute a new version of ${dst.display_name}` : `Contribute data for ${dst.display_name}`}
                    >
                      {isSubmitted ? 'Contribute New Version' : 'Contribute Data'}
                    </Button>
                  )}
                </HStack>
              </HStack>
            );
          })}
        </Stack>
      ) : (
        <Alert variant="info" title="No Data Sources Configured">
          This job has no configured data sources to contribute to. This may be a project configuration issue. Please contact an administrator.
        </Alert>
      )}
    </Stack>
  );
};

export default ContributionChecklist;